/**
 * Save a reviewed Word file back to iManage: as a new version of the open
 * document when the organization's tenant allows conditional version saves,
 * otherwise as a new document beside it. Two steps, as everywhere: prepare
 * (the server records an approval for these exact bytes) and confirm (the
 * only step that writes). A failed confirmation is never retried.
 *
 * @module components/apps/imanage/IManageSaveVersionDialog
 */
import type { IManageDocument, IManageEntry } from "@amoreg/contracts/imanage";
import { CheckCircle2Icon, FileUpIcon, Loader2Icon } from "lucide-react";
import { useRef, useState } from "react";

import { openReviewDocument } from "../integrations/imanageDocx";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import { formatBytes } from "./imanageFormat";
import {
  confirmIManageSave,
  prepareIManageSave,
  saveName,
  type PreparedIManageSave,
} from "./imanageSave";
import { InlineError } from "./IManageParts";

export function IManageSaveVersionDialog({
  organizationId,
  library,
  document,
  folder,
  canSaveVersion,
  onClose,
  onSaved,
}: {
  readonly organizationId: string;
  readonly library: string;
  readonly document: IManageDocument;
  readonly folder: Pick<IManageEntry, "id" | "name"> | null;
  readonly canSaveVersion: boolean;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}) {
  const mode: "version" | "document" =
    canSaveVersion && document.revision ? "version" : "document";
  const [file, setFile] = useState<{ bytes: Uint8Array; name: string } | null>(null);
  // A copy beside the original gets a name that tells them apart.
  const [name, setName] = useState(
    mode === "document" ? `${saveName(document.name)} (reviewed)` : saveName(document.name),
  );
  const [prepared, setPrepared] = useState<PreparedIManageSave | null>(null);
  const [receipt, setReceipt] = useState<IManageEntry | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const blocked = mode === "document" && !folder;

  const choose = async (picked: File | undefined) => {
    if (!picked) return;
    setError(null);
    setPrepared(null);
    try {
      if (picked.size > 12 * 1024 * 1024) throw new Error("The file exceeds 12 MB.");
      const bytes = new Uint8Array(await picked.arrayBuffer());
      await openReviewDocument(bytes);
      setFile({ bytes, name: picked.name });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Choose a Word .docx file.");
    }
  };

  const prepare = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      setPrepared(
        await prepareIManageSave(
          organizationId,
          {
            libraryId: library,
            documentId: document.id,
            revision: document.revision,
            mode,
            folderId: folder?.id ?? "",
            name: name.trim(),
          },
          file.bytes,
        ),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "The save could not be prepared.");
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!prepared) return;
    setBusy(true);
    setError(null);
    try {
      setReceipt(await confirmIManageSave(organizationId, prepared));
    } catch (e) {
      setError(
        `${e instanceof Error ? e.message : "The save outcome is unknown."} Check iManage before trying again. Reference: ${prepared.id}`,
      );
    } finally {
      setPrepared(null);
      setBusy(false);
      onSaved();
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) onClose();
      }}
    >
      <DialogPopup className="max-w-md" data-imanage-save-dialog="">
        <DialogHeader>
          <DialogTitle>{mode === "version" ? "Save a new version" : "Save a copy to iManage"}</DialogTitle>
          <DialogDescription>
            {mode === "version"
              ? `Upload the reviewed Word file. It becomes version ${(document.version ?? 0) + 1} of “${document.name}”.`
              : `Upload the reviewed Word file. It is saved as a new document${folder ? ` in ${folder.name}` : ""}; “${document.name}” is not changed.`}
          </DialogDescription>
        </DialogHeader>
        <DialogPanel className="space-y-4">
          {receipt ? (
            <div role="status" className="flex items-start gap-2.5 rounded-lg border border-success/40 bg-success/5 p-3 text-sm">
              <CheckCircle2Icon aria-hidden className="mt-0.5 size-4 text-success" />
              <span>
                {mode === "version"
                  ? `Saved “${receipt.name}” as version ${receipt.version ?? "—"}.`
                  : `Saved “${receipt.name}”${folder ? ` in ${folder.name}` : ""}.`}{" "}
                <span className="font-mono text-xs text-muted-foreground">{receipt.id}</span>
              </span>
            </div>
          ) : prepared ? (
            <div className="space-y-2 rounded-lg border border-border p-3 text-sm" role="region" aria-label="Confirm save">
              <p className="font-medium">
                {mode === "version"
                  ? `Save as version ${(document.version ?? 0) + 1} of #${document.documentNumber ?? document.id}?`
                  : `Save “${prepared.target.name}” in ${folder?.name}?`}
              </p>
              <p className="text-muted-foreground">
                Only this file is sent. Earlier versions stay in iManage.
              </p>
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer">File fingerprint</summary>
                <code className="break-all">{prepared.hash}</code>
              </details>
            </div>
          ) : (
            <>
              {blocked ? (
                <p className="text-sm text-muted-foreground">
                  New-version saves are not enabled for this iManage tenant yet. Open the
                  document from its folder to save a copy there.
                </p>
              ) : null}
              <button
                type="button"
                disabled={busy || blocked}
                onClick={() => input.current?.click()}
                className="flex w-full items-center gap-3 rounded-lg border border-dashed border-border px-3 py-3 text-left text-sm outline-none transition-colors hover:bg-accent/50 focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
              >
                <FileUpIcon aria-hidden className="size-5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1">
                  {file ? (
                    <>
                      <span className="block truncate font-medium">{file.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {formatBytes(file.bytes.length)} · choose another
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="block font-medium">Choose the reviewed Word file</span>
                      <span className="text-xs text-muted-foreground">.docx, up to 12 MB, no macros</span>
                    </>
                  )}
                </span>
              </button>
              <input
                ref={input}
                type="file"
                accept=".docx"
                className="hidden"
                onChange={(event) => {
                  void choose(event.target.files?.[0]);
                  event.target.value = "";
                }}
              />
              {mode === "document" && !blocked ? (
                <label className="block space-y-1.5 text-sm">
                  <span className="text-muted-foreground">Document name</span>
                  <Input value={name} maxLength={200} onChange={(e) => setName(e.target.value)} disabled={busy} />
                </label>
              ) : null}
            </>
          )}
          {error ? <InlineError message={error} /> : null}
        </DialogPanel>
        <DialogFooter>
          {receipt ? (
            <Button size="sm" onClick={onClose}>
              Done
            </Button>
          ) : prepared ? (
            <>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => setPrepared(null)}>
                Back
              </Button>
              <Button size="sm" disabled={busy} onClick={() => void confirm()} data-imanage-confirm-save="">
                {busy ? <Loader2Icon aria-hidden className="animate-spin" /> : null}
                Confirm and save
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="ghost" disabled={busy} onClick={onClose}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={busy || !file || blocked || !name.trim()}
                onClick={() => void prepare()}
              >
                {busy ? <Loader2Icon aria-hidden className="animate-spin" /> : null}
                Review save
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
